import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export interface PendingApprovalResponse {
    id: string; // queue msg_id
    uuid: string; // backend transaction uuid
    transaction_type: string;
    enum?: string | null;
    total_amount: number;
    submitted_by: number;
    submitted_by_name: string;
    submitted_at: string;
    payload: Record<string, any>;
    ref_account_number?: string;
    ref_account_name?: string;
    customer_number?: string;
    customer_name?: string;
    approval_level?: number;
    submitter_level?: number;
    max_approval_level?: number;
    total_approval_steps?: number;
}

export interface PendingApprovalsListResponse {
    transactions: PendingApprovalResponse[];
    count: number;
}

export interface ApprovalLevelsResponse {
    approval_levels: number[];
    max_level: number;
}

export interface UnifiedTransactionResponse {
    message: string;
    data: any;
    requires_approval?: boolean;
    approval_level?: number;
    transaction_id?: string;
    result?: any;
}

export const unifiedTransactionService = {
    // Submit a transaction
    async submitTransaction(
        transactionType: 'RECEIPT' | 'PAYMENT' | 'WITHDRAWAL' | 'JOURNAL',
        totalAmount: number,
        payload: any
    ): Promise<UnifiedTransactionResponse> {
        try {
            const response: ApiResponse<UnifiedTransactionResponse> = (
                await apiClient.post("/v1/unified-transaction/action", {
                    action: "SUBMIT",
                    transaction_type: transactionType,
                    payload,
                    totalAmount
                })
            ).data;
            if (response.status !== 200) {
                throw new Error(response.message || 'Transaction submission failed');
            }
            window.dispatchEvent(new CustomEvent('transaction-submitted', { detail: { type: transactionType } }));
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.detail || error.message || 'Failed to submit transaction');
        }
    },

    // Get user's approval levels
    async getUserApprovalLevels(): Promise<ApprovalLevelsResponse> {
        try {
            const response: ApiResponse<ApprovalLevelsResponse> = (
                await apiClient.get("/v1/unified-transaction/approval/levels")
            ).data;
            return response.data;
        } catch (error: any) {
            return { approval_levels: [], max_level: 0 };
        }
    },

    // Get pending approvals for a level
    async getPendingApprovals(level: number, ownOnly: boolean = false): Promise<PendingApprovalsListResponse> {
        try {
            const response: ApiResponse<PendingApprovalsListResponse> = (
                await apiClient.get(`/v1/unified-transaction/approval/pending?level=${level}&own_only=${ownOnly}`)
            ).data;
            return response.data;
        } catch (error: any) {
            return { transactions: [], count: 0 };
        }
    },

    // Approve or Reject
    async processApprovalAction(
        action: 'APPROVE' | 'REJECT',
        transactionId: string,
        msgId: string,
        approvalLevel: number,
        notes?: string,
        username?: string,
        password?: string
    ): Promise<any> {
        try {
            const body: any = {
                action,
                transaction_id: transactionId,
                msg_id: msgId,
                approval_level: approvalLevel,
                notes
            };
            if (username) body.username = username;
            if (password) body.password = password;
            const response: ApiResponse<any> = (
                await apiClient.post("/v1/unified-transaction/action", body)
            ).data;
            if (response.status && response.status !== 200) {
                throw new Error(response.message || `Failed to ${action.toLowerCase()} transaction`);
            }
            return response;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.response?.data?.detail || error.message || `Failed to ${action.toLowerCase()} transaction`);
        }
    },

    // Execute an already approved transaction
    async executeTransaction(transactionId: string, transactionType: string): Promise<any> {
        try {
            const response: ApiResponse<any> = (
                await apiClient.post("/v1/unified-transaction/action", {
                    action: "EXECUTE",
                    transaction_id: transactionId,
                    transaction_type: transactionType
                })
            ).data;
            if (response.status && response.status !== 200) {
                throw new Error(response.message || 'Failed to execute transaction');
            }
            return response;
        } catch (error: any) {
            throw new Error(error.response?.data?.detail || error.message || 'Failed to execute transaction');
        }
    },
    
    // Get cached transactions
    async getCachedTransactions(transactionType: string, state: 'pending' | 'approve' | 'reject'): Promise<any> {
        try {
            const response: ApiResponse<any> = (
                await apiClient.get(`/v1/unified-transaction/cache/${transactionType}/${state}`)
            ).data;
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.detail || 'Failed to fetch cached transactions');
        }
    },

    // Get aggregated approval counts by type/status
    async getApprovalCounts(branchId?: number): Promise<Record<string, { pending: number; approved: number; rejected: number }>> {
        try {
            const params = branchId ? `?branch_id=${branchId}` : '';
            const response: ApiResponse<any> = (
                await apiClient.get(`/v1/unified-transaction/approval/counts${params}`)
            ).data;
            return response.data || {};
        } catch (error: any) {
            return {};
        }
    },

    // Check if a transaction amount needs approval
    async checkAllowance(
        transactionType: 'RECEIPT' | 'PAYMENT' | 'WITHDRAWAL' | 'JOURNAL',
        totalAmount: number
    ): Promise<{ needs_approval: boolean; allowance_amount: number | null; total_amount: number }> {
        try {
            const response: ApiResponse<any> = (
                await apiClient.get(`/v1/unified-transaction/check-allowance`, {
                    params: { transaction_type: transactionType, total_amount: totalAmount }
                })
            ).data;
            return response.data || { needs_approval: false, allowance_amount: null, total_amount: totalAmount };
        } catch (error: any) {
            return { needs_approval: false, allowance_amount: null, total_amount: totalAmount };
        }
    },

    // Get recent transactions for the current user (paginated, filtered by working date)
    async getRecentTransactions(limit: number = 10, offset: number = 0): Promise<{ transactions: any[]; count: number }> {
        try {
            const response: ApiResponse<any> = (
                await apiClient.get(`/v1/unified-transaction/recent?limit=${limit}&offset=${offset}`)
            ).data;
            return response.data || { transactions: [], count: 0 };
        } catch (error: any) {
            return { transactions: [], count: 0 };
        }
    },

    // Get rejected transactions for the current user from the DB
    async getRejectedTransactions(limit: number = 10): Promise<{ transactions: any[]; count: number }> {
        try {
            const response: ApiResponse<any> = (
                await apiClient.get(`/v1/unified-transaction/rejected?limit=${limit}`)
            ).data;
            return response.data || { transactions: [], count: 0 };
        } catch (error: any) {
            return { transactions: [], count: 0 };
        }
    },

    // Get pending transactions for a branch (day-end)
    async getBranchPendingTransactions(branchId: number): Promise<{ count: number; transactions: any[] }> {
        try {
            const response: ApiResponse<any> = (
                await apiClient.get(`/v1/unified-transaction/approval/branch-pending/${branchId}`)
            ).data;
            return response.data || { count: 0, transactions: [] };
        } catch (error: any) {
            return { count: 0, transactions: [] };
        }
    },

    // Get transaction status
    async getTransactionStatus(transactionId: string): Promise<any> {
        try {
            const response: ApiResponse<any> = (
                await apiClient.get(`/v1/unified-transaction/status/${transactionId}`)
            ).data;
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.detail || 'Failed to get transaction status');
        }
    },

    // Get print payload by transaction header ID
    async getPrintPayload(headerId: number): Promise<{ transaction_number: string; entered_date?: string; updated_date?: string; payload: any } | null> {
        try {
            const response: ApiResponse<any> = (
                await apiClient.get(`/transaction/print-payload/${headerId}`)
            ).data;
            return response.data || null;
        } catch (error: any) {
            return null;
        }
    }
};
